import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import Navbar from "../components/qhearts/Navbar";
import ProductCard from "../components/qhearts/ProductCard";
import ComingSoonDialog from "../components/qhearts/ComingSoonDialog";
import { PRODUCTS } from "../data/products";
import { Toaster } from "../components/ui/sonner";

const Overline = ({ children }) => (
  <span className="text-xs uppercase tracking-[0.22em] font-semibold text-qh-rose">
    {children}
  </span>
);

export default function Shop() {
  const [showComingSoon, setShowComingSoon] = useState(false);

  return (
    <div
      className="bg-white text-qh-ink min-h-screen font-body"
      data-testid="shop-page"
    >
      <Toaster richColors position="top-center" />
      <Navbar onCTAClick={() => setShowComingSoon(true)} />
      <ComingSoonDialog open={showComingSoon} onOpenChange={setShowComingSoon} />

      {/* HERO */}
      <section
        className="relative pt-40 sm:pt-48 pb-12 sm:pb-16 px-6 sm:px-8 lg:px-12 overflow-hidden"
        data-testid="shop-hero"
      >
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#FFFBFB] via-white to-white" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <Overline>Shop</Overline>
            <h1
              className="mt-5 font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-qh-ink"
              data-testid="shop-headline"
            >
              Dein <span className="italic text-qh-rose">Icebreaker</span> für
              echte Begegnungen.
            </h1>
            <p className="mt-6 text-lg text-qh-slate leading-relaxed max-w-xl">
              QHearts helfen dir, den ersten Schritt zu machen — einfach, ehrlich
              und genau im richtigen Moment.
            </p>

            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-qh-ink">
              {[
                "Sichere Bestellung",
                "Versand deutschlandweit",
                "Individueller QR-Code",
              ].map((item) => (
                <li key={item} className="inline-flex items-center gap-2">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#FFE4E6]">
                    <Check className="w-3 h-3 text-qh-rose" strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.15, ease: "easeOut" }}
            className="relative"
          >
            <div className="relative mx-auto max-w-md aspect-square">
              <div className="absolute inset-8 rounded-full bg-[#FFFBFB] border border-[#FFE4E6]" />
              <motion.img
                src="/assets/qheart-front.png"
                alt="QHeart Vorderseite"
                draggable="false"
                initial={{ rotate: -8 }}
                animate={{ rotate: [-8, -4, -8] }}
                transition={{
                  duration: 7,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute top-[6%] left-[6%] w-[62%] select-none"
                style={{
                  filter: "drop-shadow(0 40px 50px rgba(225,29,72,0.28))",
                }}
              />
              <motion.img
                src="/assets/qheart-back.png"
                alt="QHeart Rückseite mit QR"
                draggable="false"
                initial={{ rotate: 10 }}
                animate={{ rotate: [10, 6, 10] }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute bottom-[6%] right-[6%] w-[52%] select-none"
                style={{
                  filter: "drop-shadow(0 30px 40px rgba(10,10,10,0.15))",
                }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section className="px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="max-w-2xl"
          >
            <Overline>Pakete</Overline>
            <h2 className="mt-4 font-display font-medium text-3xl sm:text-4xl tracking-tight text-qh-ink">
              Dein Schlüssel zu echten Begegnungen.
            </h2>
            <p className="mt-4 text-qh-slate leading-relaxed">
              Jedes QHeart ist ein physisches Objekt mit deinem persönlichen
              QR-Code. Wähle die Anzahl, die zu dir passt.
            </p>
          </motion.div>

          <div
            className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            data-testid="product-grid"
          >
            {PRODUCTS.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* TRUST / INFO STRIP */}
      <section className="px-6 sm:px-8 lg:px-12 pb-24">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-3 gap-4">
          {[
            {
              title: "Individuell.",
              body: "Jedes QHeart verlinkt nur auf dein Profil und deine Nachricht.",
              image: "/assets/qh-customize.jpg",
            },
            {
              title: "Real übergeben.",
              body: "Kein Screenshot, kein Link — ein Moment, den ihr beide spürt.",
              image: "/assets/qh-uebergabe.jpg",
            },
            {
              title: "Ehrlich.",
              body: "Gescannt wird, wenn die Person bereit ist. Kein Druck. Kein Spiel. Der perfekte Zeitpunkt wird selbst gewählt.",
              image: "/assets/qh-scan.jpg",
            },
          ].map((f) => (
            <div
              key={f.title}
              className="rounded-2xl border border-qh-line bg-white p-6 flex flex-col"
              data-testid={`trust-card-${f.title.replace(/\W/g, "").toLowerCase()}`}
            >
              <h3 className="font-display text-xl font-medium text-qh-ink">
                {f.title}
              </h3>
              <p className="mt-2 text-sm text-qh-slate leading-relaxed min-h-[64px]">
                {f.body}
              </p>
              <div className="mt-6 rounded-2xl overflow-hidden aspect-[2/3] bg-qh-blush">
                <img
                  src={f.image}
                  alt={f.title}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  draggable="false"
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FOOTER — short, shop version */}
      <footer className="bg-qh-ink text-white" data-testid="shop-footer">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <img
              src="/assets/qhearts-logo-dark.png"
              alt="QHearts – Keep Dating real."
              className="w-56 sm:w-72 h-auto object-contain select-none"
              draggable="false"
            />
            <p className="mt-4 font-display italic text-base sm:text-lg leading-tight whitespace-nowrap">
              Selbstbestimmt statt fremdbestimmt.
            </p>
            <p className="mt-1 font-display italic text-base sm:text-lg leading-tight whitespace-nowrap">
              Du entscheidest – nicht der Algorithmus.
            </p>
            <p className="mt-4 font-display italic text-base sm:text-lg leading-tight whitespace-nowrap text-white/80">
              Echte Begegnungen beginnen mit deiner Entscheidung.
            </p>
          </div>
          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} QHearts. Keep Dating Real.
          </div>
        </div>
      </footer>
    </div>
  );
}
